import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ReactMarkdown from "react-markdown";
import axios from "../components/api";
import {
    ONBOARDING_DEFAULT_EMAIL,
    ONBOARDING_DEFAULT_ORG_ID,
    ONBOARDING_DEFAULT_PROJECT_ID,
} from "../config/api";

const STORAGE_KEYS = {
    projectId: "onboarding_project_id",
    orgId: "onboarding_org_id",
    email: "onboarding_email",
};

const getStored = (key, fallback) => localStorage.getItem(key) || fallback || "";

const formatAgentResponse = (data) => {
    const inner = data?.response;
    if (!inner) {
        return typeof data === "string" ? data : JSON.stringify(data, null, 2);
    }
    if (inner.answer) {
        if (typeof inner.answer === "string") {
            try {
                const parsed = JSON.parse(inner.answer);
                return JSON.stringify(parsed, null, 2);
            } catch {
                return inner.answer;
            }
        }
        return JSON.stringify(inner.answer, null, 2);
    }
    return JSON.stringify(inner, null, 2);
};

export const OnboardingProjectSetup = () => {
    const [projectId, setProjectId] = useState(getStored(STORAGE_KEYS.projectId, ONBOARDING_DEFAULT_PROJECT_ID));
    const [orgId, setOrgId] = useState(getStored(STORAGE_KEYS.orgId, ONBOARDING_DEFAULT_ORG_ID));
    const [email, setEmail] = useState(getStored(STORAGE_KEYS.email, ONBOARDING_DEFAULT_EMAIL));
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [promptLoading, setPromptLoading] = useState(false);

    const saveSettings = () => {
        localStorage.setItem(STORAGE_KEYS.projectId, projectId);
        localStorage.setItem(STORAGE_KEYS.orgId, orgId);
        localStorage.setItem(STORAGE_KEYS.email, email);
        setMessage("Settings saved.");
    };

    const loadProjects = async () => {
        if (!orgId || !email) {
            setMessage("Organization ID and email are required to load projects.");
            return;
        }
        setLoading(true);
        setMessage("");
        try {
            const { data } = await axios.get("/projects/", {
                params: { organization_id: orgId, email },
            });
            setProjects(data);
            if (data.length > 0 && !projectId) {
                setProjectId(data[0].id);
            }
            setMessage(`Loaded ${data.length} project(s).`);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Failed to load projects.");
        } finally {
            setLoading(false);
        }
    };

    const generateRoutingPrompt = async () => {
        if (!projectId) {
            setMessage("Select a project first.");
            return;
        }
        setPromptLoading(true);
        setMessage("");
        try {
            const { data } = await axios.post("/generate-agent-system-prompt", {
                description: "Business that answers customer questions and schedules appointments.",
                project_id: projectId,
            });
            setMessage("Routing prompt generated and saved to project.");
            console.log(data);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Failed to generate routing prompt.");
        } finally {
            setPromptLoading(false);
        }
    };

    return (
        <Card sx={{ width: "100%", maxWidth: 720, margin: "auto", padding: 3, backgroundColor: "#282c34", color: "white" }}>
            <CardContent>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>OnboardingX — Project Setup</Typography>
                <TextField
                    fullWidth
                    label="Organization ID"
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ style: { color: "#94A3B8" } }}
                    InputProps={{ style: { color: "white" } }}
                />
                <TextField
                    fullWidth
                    label="User Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ style: { color: "#94A3B8" } }}
                    InputProps={{ style: { color: "white" } }}
                />
                <TextField
                    fullWidth
                    label="Project ID"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ style: { color: "#94A3B8" } }}
                    InputProps={{ style: { color: "white" } }}
                />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    <Button variant="contained" onClick={saveSettings} sx={{ bgcolor: "#EE3CFF" }}>Save</Button>
                    <Button variant="outlined" onClick={loadProjects} disabled={loading} sx={{ color: "white", borderColor: "#EE3CFF" }}>
                        {loading ? <CircularProgress size={20} /> : "Load Projects"}
                    </Button>
                    <Button variant="outlined" onClick={generateRoutingPrompt} disabled={promptLoading} sx={{ color: "white", borderColor: "#EE3CFF" }}>
                        {promptLoading ? <CircularProgress size={20} /> : "Generate Route Prompt"}
                    </Button>
                </Box>
                {projects.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        {projects.map((p) => (
                            <Button
                                key={p.id}
                                size="small"
                                onClick={() => setProjectId(p.id)}
                                sx={{ mr: 1, mb: 1, color: projectId === p.id ? "#EE3CFF" : "#94A3B8" }}
                            >
                                {p.name}
                            </Button>
                        ))}
                    </Box>
                )}
                {message && <Typography variant="body2" sx={{ color: "#94A3B8" }}>{message}</Typography>}
            </CardContent>
        </Card>
    );
};

const OnboardingChat = () => {
    const [messages, setMessages] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const scrollRef = useRef(null);
    const projectId = getStored(STORAGE_KEYS.projectId, ONBOARDING_DEFAULT_PROJECT_ID);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const sendMessage = async () => {
        if (!query.trim() || loading) return;
        if (!projectId) {
            setErrorMessage("Set a Project ID in Project Setup first.");
            return;
        }

        const userMessage = query.trim();
        setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
        setQuery("");
        setLoading(true);
        setErrorMessage("");

        try {
            const { data } = await axios.post("/route-query", {
                user_input: userMessage,
                project_id: projectId,
            });
            const reply = formatAgentResponse(data);
            setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const text = typeof detail === "string" ? detail : JSON.stringify(detail || err.message);
            setErrorMessage(text);
            setMessages((prev) => [...prev, { role: "assistant", content: `**Error:** ${text}` }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{ width: "100%", height: "calc(100vh - 120px)", margin: "auto", backgroundColor: "#282c34", color: "white", display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>OnboardingX — Agent Router</Typography>
                <Typography variant="body2" sx={{ color: "#94A3B8", mb: 2 }}>
                    Routes your question to KnowledgeX or SchedulerX. Project: {projectId || "not set"}
                </Typography>

                <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", mb: 2, pr: 1 }}>
                    {messages.length === 0 && (
                        <Typography sx={{ color: "#94A3B8" }}>
                            Try: &quot;What services do you offer?&quot; or &quot;Book an appointment tomorrow at 2pm&quot;
                        </Typography>
                    )}
                    {messages.map((msg, index) => (
                        <Box key={index} sx={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", mb: 1 }}>
                            <Paper
                                sx={{
                                    p: 2,
                                    maxWidth: "85%",
                                    backgroundColor: msg.role === "user" ? "#EE3CFF" : "#1e2229",
                                    color: "white",
                                }}
                            >
                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </Paper>
                        </Box>
                    ))}
                    {loading && <CircularProgress size={24} sx={{ color: "#EE3CFF" }} />}
                </Box>

                {errorMessage && <Typography color="error" sx={{ mb: 1 }}>{errorMessage}</Typography>}

                <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                        fullWidth
                        placeholder="Ask a knowledge or scheduling question..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                        InputProps={{ style: { color: "white" } }}
                        sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#94A3B8" } } }}
                    />
                    <Button variant="contained" onClick={sendMessage} disabled={loading} sx={{ bgcolor: "#EE3CFF", minWidth: 56 }}>
                        <SendIcon />
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default OnboardingChat;
